var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Tremor Toast [v0.0.4]
import * as ToastPrimitives from '@radix-ui/react-toast';
import { RiCheckboxCircleFill, RiCloseCircleFill, RiErrorWarningFill, RiInformationFill, RiLoader2Fill } from '@remixicon/react';
import { cx } from '@web/utility';
import React from 'react';
import { useTranslation } from 'react-i18next';
var ToastProvider = ToastPrimitives.Provider;
ToastProvider.displayName = 'ToastProvider';
var ToastViewport = React.forwardRef(function (_a, forwardedRef) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx(ToastPrimitives.Viewport, __assign({ ref: forwardedRef, className: cx('fixed right-0 top-0 z-[9999] m-0 flex w-full max-w-[100vw] list-none flex-col gap-2 p-[var(--viewport-padding)] [--viewport-padding:_15px] sm:max-w-md sm:gap-4', className) }, props)));
});
ToastViewport.displayName = 'ToastViewport';
var Toast = React.forwardRef(function (_a, forwardedRef) {
    var className = _a.className, variant = _a.variant, title = _a.title, description = _a.description, action = _a.action, _b = _a.disableDismiss, disableDismiss = _b === void 0 ? false : _b, props = __rest(_a, ["className", "variant", "title", "description", "action", "disableDismiss"]);
    var Icon;
    var t = useTranslation().t;
    switch (variant) {
        case 'success':
            Icon = (_jsx(RiCheckboxCircleFill, { className: "size-5 shrink-0 text-emerald-600 dark:text-emerald-500", "aria-hidden": "true" }));
            break;
        case 'warning':
            Icon = (_jsx(RiErrorWarningFill, { className: "size-5 shrink-0 text-amber-500 dark:text-amber-500", "aria-hidden": "true" }));
            break;
        case 'error':
            Icon = (_jsx(RiCloseCircleFill, { className: "size-5 shrink-0 text-red-600 dark:text-red-500", "aria-hidden": "true" }));
            break;
        case 'loading':
            Icon = (_jsx(RiLoader2Fill, { className: "size-5 shrink-0 animate-spin text-gray-600 dark:text-gray-500", "aria-hidden": "true" }));
            break;
        default:
            Icon = (_jsx(RiInformationFill, { className: "size-5 shrink-0 text-blue-500 dark:text-blue-500", "aria-hidden": "true" }));
            break;
    }
    return (_jsxs(ToastPrimitives.Root, __assign({ ref: forwardedRef, className: cx(
        // base
        'flex h-fit min-h-16 w-full overflow-hidden rounded-md border shadow-lg shadow-black/5', 
        // background color
        'bg-white dark:bg-[#090E1A]', 
        // border color
        'border-gray-200 dark:border-gray-800', 
        // swipe
        'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none', 
        // transition
        'data-[state=open]:animate-slideLeftAndFade', 'data-[state=closed]:animate-hide', className), "tremor-id": "tremor-raw" }, props, { children: [_jsxs("div", __assign({ className: cx(
                // base
                'flex flex-1 items-start gap-3 p-4', 
                // border
                !disableDismiss || action
                    ? 'border-r border-gray-200 dark:border-gray-800'
                    : '') }, { children: [Icon, _jsxs("div", __assign({ className: "flex flex-col gap-1" }, { children: [title && (_jsx(ToastPrimitives.Title, __assign({ className: "text-sm font-semibold text-gray-900 dark:text-gray-50" }, { children: title }))), description && (_jsx(ToastPrimitives.Description, __assign({ className: "text-sm text-gray-600 dark:text-gray-400" }, { children: description })))] }))] })), _jsxs("div", __assign({ className: "flex flex-col" }, { children: [action && (_jsxs(_Fragment, { children: [_jsx(ToastPrimitives.Action, __assign({ altText: action.altText, className: cx(
                                // base
                                'flex flex-1 items-center justify-center px-6 text-sm font-semibold transition-colors', 
                                // hover
                                'hover:bg-gray-50 hover:dark:bg-gray-900/30', 
                                // text color
                                'text-gray-800 dark:text-gray-100', 
                                // active
                                'active:bg-gray-100 active:dark:bg-gray-800', {
                                    'text-red-600 dark:text-red-500': variant === 'error'
                                }), onClick: function (event) {
                                    event.preventDefault();
                                    action.onClick();
                                }, type: "button" }, { children: action.label })), _jsx("div", { className: "h-px w-full bg-gray-200 dark:bg-gray-800" })] })), !disableDismiss && (_jsx(ToastPrimitives.Close, __assign({ className: cx(
                        // base
                        'flex flex-1 items-center justify-center px-6 text-sm transition-colors', 
                        // text color
                        'text-gray-600 dark:text-gray-400', 
                        // hover
                        'hover:bg-gray-50 hover:dark:bg-gray-900/30', 
                        // active
                        'active:bg-gray-100', action ? 'h-1/2' : 'h-full'), "aria-label": "Close" }, { children: t('close') })))] }))] })));
});
Toast.displayName = 'Toast';
export { Toast, ToastProvider, ToastViewport };
